import LabelPrintDialog from './LabelPrintDialog';
import { useMemo, useRef, useState } from 'react';
import { buildInventoryStockLabels } from '../utils/inventoryStockLabels';
import { printPackingLabels } from '../utils/packingLabelPrint';

export default function InventoryStockLabelPrint({ products, catalogBarcodes, onClose }) {
  const labels = useMemo(() => buildInventoryStockLabels(products, catalogBarcodes), [products, catalogBarcodes]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const [excluded, setExcluded] = useState(new Set());
  const selected = labels.filter(row => !excluded.has(row.id) && !row.reason && Number(counts[row.id]) > 0);
  const total = selected.reduce((sum, row) => sum + Number(counts[row.id]), 0);
  const invalid = labels.some(row => !excluded.has(row.id) && counts[row.id] !== undefined &&
    (!Number.isInteger(Number(counts[row.id])) || Number(counts[row.id]) < 0 || Number(counts[row.id]) > row.available || (Number(counts[row.id]) > 0 && row.reason)));
  const print = async mode => {
    if (lock.current || !total || invalid || total > 5000) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const rows = selected.flatMap(row => Array.from({ length: Number(counts[row.id]) }, () => row));
      const ok = await printPackingLabels({ dispatch_no: '' }, { rows, mode });
      if (!ok) setError('Labels were not printed. Check the printer message and retry.');
    } catch (err) { setError(err.message || 'Unable to print inventory labels.'); }
    finally { lock.current = false; setBusy(false); }
  };
  const rows = labels.map(row => ({ key: row.id, productName: row.product_name,
    barcode: row.mk_barcode, unit: row.unitText, count: row.available,
    mrp: Number.isFinite(row.unit_mrp) ? '₹' + row.unit_mrp.toFixed(2) : '-', price: '',
    disabled: Boolean(row.reason), reason: row.reason }));
  const excludedKeys = new Set([...excluded, ...labels.filter(row => row.reason).map(row => row.id)]);
  return <LabelPrintDialog order={{ dispatch_no: 'Inventory stock' }} rows={rows} errorMessage={error}
    excludedKeys={excludedKeys} counts={Object.fromEntries(labels.map(row => [row.id, counts[row.id] ?? 0]))}
    selectedCount={total} busy={busy}
    countError={invalid || total > 5000 ? 'Enter whole label counts within available stock, up to 5,000 labels per print.' : ''}
    pricingNote="Uses the same 57.5 × 40 mm label template as dispatch and packing. MRP stays fixed. Packed date is today; expiry uses the saved stock date, or 90 days from today when missing. The stakeholder discount is applied when scanned into the dispatch cart."
    onCountChange={(id, value) => { setError(''); setCounts(previous => ({ ...previous, [id]: value })); }}
    onToggle={id => setExcluded(previous => { const next = new Set(previous); if (next.has(id)) next.delete(id); else next.add(id); return next; })}
    onIncludeAll={() => setExcluded(new Set())}
    onExcludeAll={() => setExcluded(new Set(labels.map(row => row.id)))}
    onPreview={() => print('preview')} onPrint={() => print('print')} onClose={onClose} />;
}
